<template>
  <div>
    <ClinicPageHeader
      title="Perfil do profissional de saúde"
      subtitle="Gestão administrativa do corpo clínico"
    >
      <template #actions>
        <router-link
          :to="{ name: 'ClinicDoctors' }"
          class="btn btn--ghost btn--sm"
        >
          ← Voltar para profissionais de saúde
        </router-link>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Erro / médico inexistente -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar o profissional de saúde"
        message="O profissional de saúde não foi encontrado ou ocorreu um erro ao buscar os dados. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div class="card" style="margin-bottom:var(--space-6)">
          <div
            class="skel-line"
            style="width:200px;height:28px;margin-bottom:12px"
          />
          <div class="skel-line" style="width:320px;height:16px" />
        </div>
        <div class="card">
          <div
            v-for="i in 6"
            :key="i"
            class="skel-line"
            style="width:100%;height:18px;margin-bottom:14px"
          />
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <div class="card docadmin-head">
          <div class="avatar avatar--xl" :class="doctor.avatarClass">
            <img
              v-if="doctor.photo"
              :src="doctor.photo"
              :alt="doctor.name"
              loading="lazy"
              decoding="async"
            />
            <template v-else>{{ doctor.initials }}</template>
          </div>
          <div class="pid">
            <h2>{{ doctor.name }}</h2>
            <span class="role">
              {{ doctor.specialty }} · {{ doctor.crm }} · na clínica desde
              {{ profile.since }}
            </span>
            <div
              class="row"
              style="gap:var(--space-2);margin-top:var(--space-3)"
            >
              <span class="badge" :class="`badge--${statusVariant}`">
                {{ doctor.statusLabel }}
              </span>
              <span class="badge badge--info badge--no-dot">
                Repasse {{ doctor.transferPct }}%
              </span>
              <span class="badge badge--neutral badge--no-dot">
                Teleconsulta
              </span>
            </div>
          </div>
          <div class="row" style="gap:var(--space-2)">
            <router-link
              :to="{ name: 'ClinicDoctorSchedule', params: { id: routeId } }"
              class="btn btn--outline btn--sm"
            >
              Ver agenda
            </router-link>
            <!-- Configuração de horários é ação Admin (RBAC — Fase 2). -->
            <router-link
              :to="{ name: 'ClinicDoctorHours', params: { id: routeId } }"
              class="btn btn--primary btn--sm"
            >
              Configurar horários
            </router-link>
          </div>
        </div>

        <div class="crm-grid">
          <div class="stack" style="gap:var(--space-6)">
            <div class="card">
              <div class="card-head">
                <h3>Dados administrativos</h3>
                <button
                  v-if="!editingContract && canEdit('medicos')"
                  class="btn btn--ghost btn--sm"
                  data-test="edit-contract"
                  @click="startEditContract"
                >
                  Editar contrato
                </button>
              </div>

              <!-- Edição do contrato: mesmo .seg do passo 2 do cadastro -->
              <div v-if="editingContract" class="contract-form">
                <fieldset class="fieldset">
                  <span class="fieldset-title">Modelo de repasse</span>
                  <ClinicSeg
                    v-model="contract.transferModel"
                    :options="transferOptions"
                    style="align-self:flex-start"
                  />
                  <div class="form-grid" style="margin-top:var(--space-4)">
                    <div class="field">
                      <label class="label">
                        Repasse ao profissional de saúde
                      </label>
                      <input
                        v-model="contract.custom"
                        class="input"
                        :class="{ 'input--error': contractError }"
                        :readonly="contract.transferModel !== 'custom'"
                        :placeholder="
                          contract.transferModel === 'custom' ? 'Ex.: 65%' : ''
                        "
                      />
                      <span v-if="contractError" class="field-error">
                        Informe um percentual entre 1% e 99%.
                      </span>
                    </div>
                    <div class="field">
                      <label class="label">Unidade principal</label>
                      <select v-model="contract.unitId" class="select">
                        <option value="" disabled>Selecione…</option>
                        <option
                          v-for="u in unitOptions"
                          :key="u.id"
                          :value="u.id"
                        >
                          {{ u.name }}
                        </option>
                      </select>
                    </div>
                  </div>
                </fieldset>
                <div class="row contract-form__actions">
                  <button
                    class="btn btn--outline btn--sm"
                    data-test="cancel-contract"
                    :disabled="savingContract"
                    @click="editingContract = false"
                  >
                    Cancelar
                  </button>
                  <button
                    class="btn btn--primary btn--sm"
                    data-test="save-contract"
                    :disabled="savingContract"
                    @click="saveContract"
                  >
                    {{ savingContract ? "Aguarde…" : "Salvar" }}
                  </button>
                </div>
              </div>

              <div class="dl">
                <div>
                  <dt>Nome</dt>
                  <dd>{{ profile.fullName }}</dd>
                </div>
                <div>
                  <dt>Especialidade</dt>
                  <dd>{{ doctor.specialty }}</dd>
                </div>
                <div>
                  <dt>CRM</dt>
                  <dd>{{ crmNumber }} · {{ doctor.uf }}</dd>
                </div>
                <div>
                  <dt>CPF</dt>
                  <dd>{{ profile.cpf }}</dd>
                </div>
                <div>
                  <dt>Telefone</dt>
                  <dd>{{ profile.phone }}</dd>
                </div>
                <div>
                  <dt>E-mail</dt>
                  <dd>{{ profile.email }}</dd>
                </div>
                <div>
                  <dt>Modelo de repasse</dt>
                  <dd>{{ doctor.transferPct }}% por consulta realizada</dd>
                </div>
                <div>
                  <dt>Unidade</dt>
                  <dd>{{ profile.unit }}</dd>
                </div>
              </div>
            </div>

            <div class="card card--flush">
              <div class="card-head flush">
                <h3>Recebimentos a repassar</h3>
                <router-link
                  :to="{ name: 'ClinicFinanceTransfers' }"
                  class="seelink"
                >
                  Ver repasses →
                </router-link>
              </div>
              <table
                v-if="transfers.length"
                class="table"
                style="margin-top:var(--space-2)"
              >
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Consultas</th>
                    <th>A repassar</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="t in transfers" :key="t.period">
                    <td class="num">{{ t.period }}</td>
                    <td class="num">{{ t.appointments }}</td>
                    <td class="num money">{{ formatMoney(t.amountCents) }}</td>
                    <td>
                      <span class="badge" :class="`badge--${t.statusVariant}`">
                        {{ t.statusLabel }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <p
                v-else
                style="padding:var(--space-4);font-size:var(--font-size-sm);color:var(--color-gray-400);margin:0"
              >
                Repasses consolidados na tela de Financeiro › Repasses.
              </p>
            </div>
          </div>

          <div class="crm-aside">
            <div class="card">
              <span class="fieldset-title">Desempenho do mês</span>
              <div
                class="mini-stats"
                style="grid-template-columns:1fr 1fr;margin-top:var(--space-3)"
              >
                <div
                  v-for="ms in performance"
                  :key="ms.label"
                  class="mini-stat"
                  style="padding:var(--space-3)"
                >
                  <span class="v">{{ ms.value }}</span>
                  <span class="l">{{ ms.label }}</span>
                </div>
              </div>
            </div>

            <div class="card">
              <span class="fieldset-title">Especialidades atendidas</span>
              <div class="cond-chips" style="margin-top:var(--space-3)">
                <span v-for="c in profile.conditions" :key="c">{{ c }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import clinicCan from "@/mixins/clinicCan";
import { ClinicDoctors } from "@/services/clinica";
import { formatMoney } from "@/data/clinica";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";

const TRANSFER_PRESETS = ["60", "70", "80"];

// Mensagem simples da API (`render json: { error: "..." }`).
function apiErrorMessage(error) {
  const body = error && error.response && error.response.data;
  return body && typeof body.error === "string" ? body.error : "";
}

export default {
  name: "ClinicDoctorProfile",
  components: { ClinicPageHeader, ClinicStateBox, ClinicSeg },
  mixins: [clinicCan],
  data() {
    return {
      // loading → ready/error. Médico inexistente cai no estado de erro.
      state: "loading",
      doctor: null,
      formatMoney,
      // Sem endpoint de histórico de repasses por médico — não fabricar.
      // A visão de repasses fica em ClinicFinanceTransfers (link no card).
      transfers: [],
      transferOptions: [
        { value: "60", label: "60%" },
        { value: "70", label: "70%" },
        { value: "80", label: "80%" },
        { value: "custom", label: "Outro" }
      ],
      editingContract: false,
      savingContract: false,
      contractTouched: false,
      contract: { transferModel: "70", custom: "", unitId: "" }
    };
  },
  created() {
    this.fetch();
  },
  methods: {
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const { data } = await ClinicDoctors.get(this.routeId, { fail });
        this.doctor = data;
        this.state = "ready";
      } catch (e) {
        // Médico não encontrado ou falha no serviço → estado de erro.
        this.doctor = null;
        this.state = "error";
      }
    },
    reload() {
      this.fetch();
    },
    startEditContract() {
      const pct = this.doctor.transferPct;
      const preset = TRANSFER_PRESETS.includes(String(pct));
      this.contract = {
        transferModel: preset ? String(pct) : "custom",
        custom: pct ? `${pct}%` : "",
        unitId: this.doctor.unitId || ""
      };
      this.contractTouched = false;
      this.editingContract = true;
    },
    async saveContract() {
      this.contractTouched = true;
      if (this.contractPct == null || this.savingContract) return;
      this.savingContract = true;
      try {
        const { data } = await ClinicDoctors.updateContract(this.routeId, {
          transferPct: this.contractPct,
          unitId: this.contract.unitId || null
        });
        this.doctor = { ...this.doctor, ...data };
        this.editingContract = false;
        this.$toast.success("Novo percentual vale para o próximo repasse.");
      } catch (e) {
        this.$toast.error(
          apiErrorMessage(e) || "Não foi possível salvar o contrato."
        );
      } finally {
        this.savingContract = false;
      }
    }
  },
  computed: {
    unitOptions() {
      const units = this.$store.getters["clinica/units"] || [];
      return units.filter(u => u && u.id && u.name);
    },
    // % do form: preset ou "Outro" (1–99); null quando inválido.
    contractPct() {
      const raw =
        this.contract.transferModel === "custom"
          ? parseInt(this.contract.custom, 10)
          : Number(this.contract.transferModel);
      return Number.isNaN(raw) || raw < 1 || raw > 99 ? null : raw;
    },
    contractError() {
      return this.contractTouched && this.contractPct == null;
    },
    // Dados administrativos reais, derivados do médico carregado da API
    // (antes era um objeto mock hardcoded com "Renata Cristina Alves").
    profile() {
      const d = this.doctor || {};
      return {
        fullName: d.name || "—",
        cpf: d.cpf || "—",
        phone: d.phone || "—",
        email: d.email || "—",
        unit: d.unit || "—",
        since: d.since || "—",
        conditions: d.conditions && d.conditions.length ? d.conditions : []
      };
    },
    routeId() {
      return this.$route.params.id;
    },
    statusVariant() {
      return this.doctor && this.doctor.status === "ativo"
        ? "success"
        : "neutral";
    },
    crmNumber() {
      // Fixture guarda "CRM 12345"; exibimos só o número na dl.
      return this.doctor ? this.doctor.crm.replace(/^CRM\s*/, "") : "";
    },
    performance() {
      if (!this.doctor) return [];
      // Só métricas com fonte real no backend (Avaliação/Gerado eram
      // hardcoded e não têm campo — removidos).
      return [
        { label: "Consultas", value: this.doctor.appointmentsMonth },
        { label: "Ocupação", value: `${this.doctor.occupation}%` }
      ];
    }
  },
  watch: {
    routeId() {
      this.fetch();
    },
    // Preset preenche o campo (somente leitura); "Outro" deixa digitar.
    "contract.transferModel"(model) {
      if (model !== "custom") this.contract.custom = `${model}%`;
    }
  }
};
</script>

<style lang="scss" scoped>
.contract-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding-bottom: var(--space-5);
  margin-bottom: var(--space-5);
  border-bottom: 1px solid var(--color-gray-100);

  &__actions {
    justify-content: flex-end;
    gap: var(--space-2);
  }
}

.cond-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);

  span {
    font-size: var(--font-size-sm);
    font-weight: var(--fw-semibold);
    color: var(--color-gray-700);
    background: var(--color-gray-100);
    border-radius: var(--radius-full);
    padding: 6px 14px;
  }
}
</style>
